import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useCraftsCart } from "@/context/CraftsCartContext";

const CraftsSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCraftsCart();
  const [state, setState] = useState({ paid: false, downloads: [], order: null });
  const [tries, setTries] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    document.title = "Thank you · Crafts";
    if (!sessionId) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/crafts/order/${sessionId}`);
        if (!alive) return;
        setState(data);
        if (data.paid) clear();
        if (!data.paid && tries < 12) {
          timer.current = setTimeout(() => setTries((t) => t + 1), 2500);
        }
      } catch {
        if (alive && tries < 12) timer.current = setTimeout(() => setTries((t) => t + 1), 2500);
      }
    };
    poll();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [sessionId, tries]);

  return (
    <div data-testid="crafts-success" className="max-w-2xl mx-auto px-6 py-24 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-5">Thank you</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Your downloads are ready.</h1>
        {state.order?.order_number && (
          <p className="text-cream/50 mt-3 text-sm">Order {state.order.order_number} · a copy has been emailed to you.</p>
        )}

        {!state.paid ? (
          <div className="mt-12 flex flex-col items-center gap-3 text-cream/60">
            <Loader2 size={22} className="animate-spin text-gold" />
            <p className="text-sm">Confirming your payment…</p>
          </div>
        ) : state.downloads.length === 0 ? (
          <p className="mt-12 text-cream/60 text-sm">Your files will arrive by email shortly.</p>
        ) : (
          <div className="mt-12 space-y-3 text-left" data-testid="crafts-downloads">
            {state.downloads.map((d, i) => (
              <a key={i} href={mediaUrl(d.url)} target="_blank" rel="noreferrer"
                data-testid={`craft-download-${i}`}
                className="flex items-center justify-between gap-4 border border-gold/30 bg-cocoa/40 px-5 py-4 hover:border-gold transition-colors">
                <span className="text-cream text-sm">{d.title}</span>
                <span className="flex items-center gap-2 text-gold text-[0.66rem] tracking-[0.2em] uppercase"><Download size={15} /> Download</span>
              </a>
            ))}
          </div>
        )}

        {state.order?.notes?.length > 0 && (
          <div data-testid="crafts-notes" className="mt-8 border border-gold/30 bg-cocoa/40 p-4 text-left space-y-1">
            {state.order.notes.map((n, i) => <p key={i} className="text-cream/80 text-sm">{n}</p>)}
          </div>
        )}

        <Link to="/crafts" className="inline-block mt-14 border border-cream/25 text-cream/70 px-7 py-3 text-[0.66rem] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">
          Back to Crafts
        </Link>
      </motion.div>
    </div>
  );
};

export default CraftsSuccess;
